import { Component, OnInit, ViewChild, ElementRef, Input, Output, OnDestroy } from '@angular/core';
import { AngularEditorConfig } from '@kolkov/angular-editor';
import { HttpClient } from '@angular/common/http';
import { HttpService } from '../../services/http-service.service';
import { Router, ActivatedRoute } from '@angular/router';
import { BsModalService, BsModalRef } from 'ngx-bootstrap/modal';
import { EventEmitter } from '@angular/core';
import Quill from 'quill';
import BlotFormatter from 'quill-blot-formatter';
import { BlurbService } from '../../services/blurb.service';
import { PreparedStatement } from '../../model';
import { FootNote, FootNoteRef } from '../../model/prepared-statement';
Quill.register('modules/blotFormatter', BlotFormatter);

@Component({
  selector: 'app-blurb-new',
  templateUrl: './blurb-new.component.html',
  styleUrls: ['./blurb-new.component.scss']
})

export class BlurbNewComponent implements OnInit, OnDestroy {

  @ViewChild('editor') editorElementRef: any;
  @ViewChild('footnoteModal', {static: true }) footnoteModal: ElementRef;
  @Input() isFromModal;
  @Output() modalClose = new EventEmitter();

  blurbItem: PreparedStatement;
  blurbConfig: AngularEditorConfig = {
    editable: true,
    spellcheck: true,
    height: 'auto',
    minHeight: '250px',
    maxHeight: '400px',
    width: 'auto',
    minWidth: '0',
    translate: 'yes',
    enableToolbar: true,
    showToolbar: true,
    placeholder: 'Enter text here...',
    defaultParagraphSeparator: '',
    defaultFontName: '',
    defaultFontSize: '',
    fonts: [
      { class: 'arial', name: 'Arial' },
      { class: 'times-new-roman', name: 'Times New Roman' },
      { class: 'calibri', name: 'Calibri' },
      { class: 'comic-sans-ms', name: 'Comic Sans MS' }
    ],
    sanitize: true,
    toolbarPosition: 'top'
  };
  quillModules = {
    blotFormatter: {},
    toolbar: [
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'align': [] }],
      ['link', 'image'],
      ['clean'],
    ]
  };
  isEditMode: boolean = false;
  modalRef: BsModalRef;
  footnote: FootNote = new FootNote();
  showConvert: boolean = false;
  footnoteRef: FootNoteRef = new FootNoteRef();
  loading: boolean = false;
  currentSelection: number = -1;
  blurbSub: any;

  constructor(
    public httpClient: HttpClient,
    public httpService: HttpService,
    public router: Router,
    public route: ActivatedRoute,
    public modalService: BsModalService,
    private blurbService: BlurbService
  ) { 
  }

  ngOnInit(): void {
    this.blurbSub = this.blurbService.blurb$.subscribe(blurb => {
      this.blurbItem = blurb;
      if(!this.blurbItem){
        this.blurbItem = new PreparedStatement();
      }else{
        this.isEditMode = true;
      }
    });
  }

  // ngOnDestroy() - When page is destroy clear data from local storage.
  ngOnDestroy(): void {
    this.blurbService.clearBlurb();
  }

  // saveBlurb() - Save blurb
  saveBlurb(): void {
    this.loading = true;
    this.httpService.addBlurbItem(this.blurbItem).subscribe((resp) => {
      this.loading = false;
      if (resp.success) {
        if(this.isFromModal){
          let responsePS:PreparedStatement = new PreparedStatement();
          responsePS = resp.result;
          this.modalClose.emit({modalClose: true, newBlurb: responsePS, isEditMode: this.isEditMode});
        }else{
          this.clearAndNavigateBack();
        }
      }
    }, (error) => {
      this.loading = false;
      if (error) {
        //this.toastrService.error(error.message);
      }
    });
  }

  // removeBlurb() - Remove blurb
  removeBlurb(): void {
    this.httpService.removeBlurbItem(this.blurbItem).subscribe((resp) => {
      if (resp.success) {
        this.clearAndNavigateBack();
      }
    }, (error) => {
      if (error) {
       // this.toastrService.error(error.message);
      }
    });
  }

  // clearBlurbItem() - Clear blurb item.
  clearBlurbItem(): void {
    this.blurbItem.TEXT = '';
    this.blurbItem.HEADER = '';
    this.blurbItem.ID = 0;
    this.blurbItem.REFS = [];
  }

  // clearAndNavigateBack() - Clear blurb item and navigate to list page
  clearAndNavigateBack(): void {
    this.clearBlurbItem();
    this.router.navigate(['/manage/research/blurbs']);
  }

  // openFootnoteModal() - Open modal to create footness notes.
  openFootnoteModal(): void {
    const selection = this.editorElementRef.quillEditor.getSelection(); // get position of cursor (index of selection)
    if(selection)
      this.currentSelection = selection.index;

    this.getFootnoteRef();
    this.modalRef = this.modalService.show(this.footnoteModal, { backdrop: 'static', class: 'modal-width' });
  }

  // closeFootnoteModal() - Close footnote modal.
  closeFootnoteModal(): void {
    this.modalRef.hide();
    this.footnote.CODE = '';
    this.footnote.REFERENCE = '';
    this.currentSelection = -1;
  }

  // insertFootnote() - Insert footnote.
  insertFootnote(): void {
    if(this.currentSelection > -1)
      this.editorElementRef.quillEditor.clipboard.dangerouslyPasteHTML(this.currentSelection, '<sup>' + this.footnote.CODE + '</sup>', 'user');

    this.blurbItem.REFS.push(JSON.parse(JSON.stringify(this.footnote)));
    this.closeFootnoteModal();
  }

  // removeFootnote() - Remove footnote
  removeFootnote(index: number): void {
    let matchString = '<sup>' + this.blurbItem.REFS[index].CODE + '</sup>';
    let regex = new RegExp(matchString, 'g' );
    let footnoteList = (this.blurbItem.TEXT.match(regex) || []);
    if(footnoteList.length > 0) this.blurbItem.TEXT = this.blurbItem.TEXT.replace(footnoteList[0], '');
    this.blurbItem.REFS.splice(index, 1);
  }

  // checkFootnoteOnPaste() - check that paste data contains any footnote or not.
  checkFootnoteOnPaste(event): void {
    // Get list of footnotes from text
    let footnoteList = (event.match(/<a href=".*?(?=#_ftn\d)(.*?)<\/a>/g) || []);

    // If any footnotes found then show convert button
    if (footnoteList.length > 0)
      this.showConvert = true;
  }

  // convertFootnote() - Convert footnote.
  convertFootnote(): void {
    // Get list of footnotes from text
    let footnoteList = (this.blurbItem.TEXT.match(/<a href=".*?(?=#_ftn\d)(.*?)<\/a>/g) || []);
    let footNoteText = (this.blurbItem.TEXT.match(/<p class="MsoFootnoteText"><a href=.*?(?=#_ftnref)(.*?)<\/p>/g) || []);

    // If any footnotes found then show convert button
    if (footnoteList.length > 0)
      this.showConvert = true;

    // Loop through list of footnotes
    for (let i = 0; i < footnoteList.length; i++) {
      let footnoteId = footnoteList[i].match(/".*?(?=#_ftn\d)(.*?)"/g) || [];
      let footnoteCode = '';
      let footnoteRefCode = '';
      if (footnoteId.length > 0) {
        footnoteCode = footnoteId[0].replace(/"/g, '');
        let ftnIndex = footnoteCode.indexOf('ftn');
        footnoteCode = footnoteCode.substr(ftnIndex, footnoteCode.length);
        footnoteRefCode = footnoteCode.replace('ftn', 'ftnref');

        for (let j = 0; j < footNoteText.length; j++) {
          if (footNoteText[j].includes(footnoteRefCode))
            this.getFootnoteRefAsync(i, j, footNoteText, footnoteList)
              .then(()=>{})
              .catch(()=>{});
        }
      }
    }

    // TO DO : Remove all hr lines of footnote
    this.blurbItem.TEXT = this.blurbItem.TEXT.replace(/<hr align="left" size="1" width="33%">/g, '');

    // After all processing make button invisible
    this.showConvert = false;
  }

  // getFootnoteRefAsync() - Get footnote reference code and wait untill response not come
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  async getFootnoteRefAsync(i, j, footNoteText, footnoteList) {
    await this.getFootnoteRefAwait(i, j, footNoteText, footnoteList);
  }

  // getFootnoteRefAwait() - Get footnote reference code and return it to the await function
  getFootnoteRefAwait(i, j, footNoteText, footnoteList):any {
    return new Promise((resolve, reject) => {
      this.loading = true;
      // Insert This footnote in list
      this.httpService.getFootnoteRef().subscribe((resp) => {
        if (resp.success) {
          this.loading = false;
          this.footnote.CODE = resp.result.REFERENCE;
          this.footnote.REFERENCE = footNoteText[j];
          let linkRemove = (footNoteText[j].match(/<a href=".*?(?=#_ftnref)(.*?)<\/a>/g) || []);
          if (linkRemove.length > 0)
            this.footnote.REFERENCE = footNoteText[j].replace(linkRemove[0], '');

          this.blurbItem.REFS.push(JSON.parse(JSON.stringify(this.footnote)));

          // remove it from text
          let supReplaceText = '<sup>' + resp.result.REFERENCE + '</sup>';
          this.blurbItem.TEXT = this.blurbItem.TEXT.replace(footnoteList[i], supReplaceText);
          this.blurbItem.TEXT = this.blurbItem.TEXT.replace(footNoteText[j], '');

         // resolve();
        }
      }, (error) => {
        if (error) {
          //this.toastrService.error(error.message);
          reject();
        }
        this.loading = false;
      });
    });
  }

  // getFootnoteRef() - Get latest footnote reference.
  getFootnoteRef(): void {
    this.httpService.getFootnoteRef().subscribe((resp) => {
      if (resp.success) {
        this.footnote.CODE = resp.result.REFERENCE;
      }
    }, (error) => {
      if (error) {
        //this.toastrService.error(error.message);
      }
    });
  }

  // closeOuterModal - close modal when page is opened as a modal.
  closeOuterModal(): void{
    let response:any = {modalClose: true};
    this.modalClose.emit(response);
  }
}
